import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  AuthShell,
  AuthField,
  AuthPrimaryButton,
  AuthErrorBanner,
  formatAuthErrorDetail,
} from "@/components/auth/AuthShell";

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(
        formatAuthErrorDetail(err.response?.data?.detail) ||
          "Couldn't send reset email. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Reset password"
      title={sent ? "Check your inbox." : "Forgot your password?"}
      subtitle={
        sent
          ? "If an account exists for that email, we've sent a reset link. It expires in 60 minutes."
          : "Type the email on your account and we'll send you a one-time reset link."
      }
      footer={
        <>
          Remembered it?{" "}
          <Link
            to="/login"
            data-testid="forgot-login-link"
            className="text-[#E8B96C] underline underline-offset-4 decoration-[#E8B96C]/40 hover:decoration-[#E8B96C]"
          >
            Back to sign in
          </Link>
        </>
      }
    >
      {!sent ? (
        <>
          <AuthErrorBanner error={error} testId="forgot-error" />
          <form onSubmit={onSubmit} className="space-y-5">
            <AuthField
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="you@studio.com"
              autoComplete="email"
              testId="forgot-email-input"
            />
            <AuthPrimaryButton disabled={busy} testId="forgot-submit-button">
              {busy ? "Sending…" : "Send reset link"}
            </AuthPrimaryButton>
          </form>
        </>
      ) : (
        <div
          data-testid="forgot-success"
          className="px-4 py-3 border border-[#E8B96C]/25 bg-[#E8B96C]/[0.06] rounded-[4px] text-sm text-[#F4EBDC]"
        >
          A reset link is on its way to <span className="text-[#E8B96C]">{email}</span>. Give it a
          minute, then check your spam folder if you don't see it.
        </div>
      )}
    </AuthShell>
  );
}
